// Training fixture: 12 hand-written claims (CLAIMS_UI_BUILD_SPEC §14.3) plus a
// generated "Ready to confirm" tail so the queue's load-more is demoable at
// scale (00-ds-09 §2, D148). Autopilot fixture (§14.4). Hand-written copy is
// reproduced word for word.
import { DENTIST_APPROVAL_HOLD, type Claim } from './types';
import { generate, pick, int, money, fullName, CDT } from '@/components/lists';
import { PMS } from '@/features/shared/pms';

/** Payers as {name, short} pairs for the generated ready tail. */
const READY_PAYERS = [
  { name: 'Aetna PPO', short: 'Aetna' },
  { name: 'Delta Dental PPO', short: 'Delta' },
  { name: 'Cigna DPPO', short: 'Cigna' },
  { name: 'MetLife PDP', short: 'MetLife' },
  { name: 'Guardian', short: 'Guardian' },
  { name: 'UnitedHealthcare', short: 'UnitedHealthcare' },
] as const;
const READY_PROVIDERS = ['Dr. Emily Carter', 'Dr. Olivia Brooks'] as const;
const READY_TEETH: (string | null)[] = ['#3', '#14', '#19', '#30', null, '#2', '#18'];
const READY_MEMBER_PREFIX = ['W281', '0917', 'U77', 'UC', '6120'] as const;

/**
 * A deterministic tail of plain "Ready to confirm" claims (ELVA checked them and
 * changed nothing). Seeded, so the queue always shows the same rows. These keep
 * the real Claim shape so each is navigable in the review workspace.
 */
function buildReadyFiller(count: number): Claim[] {
  return generate(count, 148002, (r, i): Claim => {
    const payer = pick(r, READY_PAYERS);
    const n = int(r, 1, 2);
    const checked = int(r, 40, 320);
    const procedures = Array.from({ length: n }, (_u, k) => {
      const cdt = pick(r, CDT);
      return {
        id: `p-rf${String(i)}-${String(k)}`,
        code: cdt.code,
        description: cdt.desc,
        tooth: pick(r, READY_TEETH),
        fee: Math.round(money(r, 90, 1480)),
      };
    });
    // Nothing is dated after today (Oct 18, 2026).
    const mm = r() < 0.5 ? '09' : '10';
    const dd = String(int(r, 1, mm === '10' ? 18 : 28)).padStart(2, '0');
    const serviceDate = `2026-${mm}-${dd}`;
    const memberId = `${pick(r, READY_MEMBER_PREFIX)}-${String(int(r, 10, 99))}-${String(int(r, 1000, 9999))}`;
    const base: Claim = {
      id: `CLM-RF${String(2000 + i)}`,
      // The scripted demos own Sofia and Mateo; random patients never reuse them.
      patient: fullName(r).replace(/^(Sofia|Mateo) /, 'Nadia '),
      memberId,
      payer: { name: payer.name, short: payer.short },
      serviceDate,
      provider: pick(r, READY_PROVIDERS),
      source: { kind: 'pms_draft', createdAt: 'at checkout', reviewedAt: 'the same day' },
      usuallyPays: Math.round(money(r, 120, 1300)),
      procedureGroup: 'checked',
      procedures,
      changes: [],
      checkedAgainst: {
        sentence: `Codes and tooth match ${payer.short}’s rules and how ${payer.short} paid your last ${String(checked)} claims like this.`,
        label: `${String(checked)} ${payer.short} claims`,
        evidenceId: 'ev-ready-filler',
      },
      attachments: [],
      remarks: '',
      listLine: `Checked against ${String(checked)} ${payer.short} claims`,
      history: [`${PMS} draft created at checkout`, 'ELVA reviewed it the same day'],
    };
    // The demo claim where the payer will pay the patient (D197, H2 §3): Dr. Lee
    // is out of network with MetLife and the PMS record has no assignment of
    // benefits. It replaces one generated Ready claim, so the queue keeps its 40.
    return i === 0 ? { ...base, ...PAYS_PATIENT_DEMO } : base;
  });
}

const PAYS_PATIENT_DEMO: Partial<Claim> = {
  patient: 'Nora Whitfield',
  memberId: '553-31-0418-01',
  payer: { name: 'MetLife PDP', short: 'MetLife' },
  provider: 'Dr. Marcus Lee',
  serviceDate: '2026-10-16',
  usuallyPays: 640,
  procedures: [
    { id: 'p-rf0-0', code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#19', fee: 1295 },
  ],
  checkedAgainst: {
    sentence: 'Codes and tooth match MetLife’s rules and how MetLife paid your last 96 claims like this.',
    label: '96 MetLife claims',
    evidenceId: 'ev-ready-filler',
  },
  listLine: 'Checked against 96 MetLife claims',
  assignmentOfBenefits: 'none',
};

function pmsHistory(created: string, reviewed: string): string[] {
  return [`${PMS} draft created ${created}`, `ELVA reviewed ${reviewed}`];
}
function createdHistory(reviewed: string): string[] {
  return [`ELVA built the claim ${reviewed}`, `ELVA reviewed ${reviewed}`];
}

export function buildTrainingClaims(): Claim[] {
  return [
    // 1 — Maria Lopez
    {
      id: 'CLM-1012',
      patient: 'Maria Lopez',
      memberId: 'W281-44-0917',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-10-12',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Oct 12, 4:52 PM', reviewedAt: 'Oct 12, 5:03 PM' },
      usuallyPays: 1180,
      procedureGroup: 'crown',
      procedures: [
        { id: 'p-1012-1', code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#3', fee: 1295 },
        { id: 'p-1012-2', code: 'D2950', description: 'Core build-up, including any pins', tooth: '#3', fee: 285, elvaTouched: true },
      ],
      decision: {
        title: 'The periodontal chart is 14 months old',
        body:
          `Aetna denies a build-up billed with a crown unless a periodontal chart is attached — 9 of 9 of your claims without one were denied. The latest chart in ${PMS} is from Aug 2025. Aetna usually wants one from the last 12 months.`,
        options: [
          {
            id: 'opt-attach',
            label: 'Attach the Aug 2025 chart anyway',
            style: 'secondary',
            effect: 'attach',
            attachment: {
              id: 'att-1012-perio',
              name: 'Periodontal chart',
              kind: 'document',
              date: 'Aug 14, 2025',
              origin: `from ${PMS}`,
            },
            resolvedText: 'attach the Aug 2025 chart anyway',
          },
          {
            id: 'opt-hold',
            label: 'Hold until we chart again',
            style: 'secondary',
            effect: 'hold',
            holdReason: 'Waiting for a document',
            holdNote: 'New periodontal chart',
            resolvedText: 'hold until we chart again',
          },
          {
            id: 'opt-bill',
            label: 'Bill without it',
            style: 'ghost',
            effect: 'none',
            resolvedText: 'bill without the periodontal chart',
          },
        ],
        evidence: [
          { id: 'ev-aetna-perio', label: '9 denied Aetna claims' },
          { id: 'att-1012-perio', label: 'Perio chart, Aug 14 2025' },
        ],
      },
      changes: [
        {
          id: 'ch-1012-1',
          title: 'Added tooth #3 to the build-up',
          why: `${PMS}’s draft had no tooth on D2950. Taken from the crown on the same tooth.`,
          applied: true,
          revert: {
            before: { procedures: [{ id: 'p-1012-2', tooth: null, elvaTouched: false }] },
            after: { procedures: [{ id: 'p-1012-2', tooth: '#3', elvaTouched: true }] },
          },
        },
        {
          id: 'ch-1012-2',
          title: 'Attached the periapical X-ray from Oct 12',
          why: 'Aetna requires an X-ray for a crown. Found in the imaging taken that day.',
          evidenceId: 'att-1012-xray',
          evidenceLabel: 'View X-ray',
          applied: true,
          revert: {
            before: { removeAttachmentId: 'att-1012-xray' },
            after: {
              addAttachment: {
                id: 'att-1012-xray',
                name: 'Periapical X-ray',
                kind: 'xray',
                date: 'Oct 12, 2026',
                origin: 'added by ELVA',
              },
            },
          },
          undoWarning:
            'Aetna requires an X-ray for a crown. Without one this claim will likely be denied.',
        },
        {
          id: 'ch-1012-3',
          title: 'Wrote a clinical narrative for the build-up',
          why: 'Aetna requires one when D2950 is billed with a crown. Read it below and edit if needed.',
          applied: true,
          revert: {
            before: { narrativeText: '' },
            after: {
              narrativeText:
                'Tooth #3 has extensive distal and occlusal caries with loss of more than half of the coronal structure. The remaining tooth cannot retain a crown without a core build-up. The periapical X-ray of Oct 12, 2026 shows the extent of the decay and no periapical pathology. A build-up followed by a full-coverage crown is needed to restore function.',
            },
          },
          undoWarning:
            'Aetna requires a narrative for D2950 billed with a crown. Write one below or the claim may be denied.',
        },
      ],
      narrative: {
        code: 'D2950',
        state: 'elva',
        basis: 'the clinical note and X-ray of Oct 12',
        text:
          'Tooth #3 has extensive distal and occlusal caries with loss of more than half of the coronal structure. The remaining tooth cannot retain a crown without a core build-up. The periapical X-ray of Oct 12, 2026 shows the extent of the decay and no periapical pathology. A build-up followed by a full-coverage crown is needed to restore function.',
        alt:
          'Tooth #3 presents with recurrent decay on the distal and occlusal surfaces; after caries removal, less than half of the clinical crown remains. A core build-up is required to provide retention and resistance form for the planned porcelain/ceramic crown. See the periapical X-ray of Oct 12, 2026.',
      },
      attachments: [
        { id: 'att-1012-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 12, 2026', origin: 'added by ELVA' },
        { id: 'att-1012-photo', name: 'Intraoral photo', kind: 'photo', date: 'Oct 12, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Perio chart is 14 months old',
      history: pmsHistory('Oct 12, 4:52 PM', 'Oct 12, 5:03 PM'),
    },

    // 2 — Sarah Mitchell
    {
      id: 'CLM-1006A',
      patient: 'Sarah Mitchell',
      memberId: '0917-55-2041',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      serviceDate: '2026-10-06',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Oct 6, 3:18 PM', reviewedAt: 'Oct 6, 3:30 PM' },
      usuallyPays: 1610,
      procedureGroup: 'implant',
      procedures: [
        { id: 'p-1006a-1', code: 'D6010', description: 'Implant body, endosteal', tooth: '#30', fee: 2140 },
      ],
      changes: [
        {
          id: 'ch-1006a-1',
          title: 'Added the pre-auth number PA-3299',
          why: 'Delta requires it on an implant claim. Taken from the pre-auth Delta approved on Sep 18.',
          evidenceId: 'ev-pa-3299',
          evidenceLabel: 'Pre-auth PA-3299',
          applied: true,
          revert: {
            before: { remarks: '' },
            after: { remarks: 'Pre-auth PA-3299' },
          },
          undoWarning: 'Delta will deny an implant claim without its pre-auth number.',
        },
      ],
      attachments: [
        { id: 'att-1006a-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 6, 2026', origin: `from ${PMS}` },
        { id: 'att-1006a-pa', name: 'Pre-auth approval PA-3299', kind: 'document', date: 'Sep 18, 2026', origin: `from ${PMS}` },
      ],
      remarks: 'Pre-auth PA-3299',
      listLine: 'Added the pre-auth number',
      history: pmsHistory('Oct 6, 3:18 PM', 'Oct 6, 3:30 PM'),
    },

    // 3 — Lucia Ortiz (ELVA created)
    {
      id: 'CLM-1006B',
      patient: 'Lucia Ortiz',
      memberId: '0917-80-1123',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      serviceDate: '2026-10-06',
      provider: 'Dr. Emily Carter',
      source: {
        kind: 'elva_created',
        reviewedAt: 'Oct 7',
        note:
          `Three procedures were completed on Oct 6 and never billed. ELVA built the claim from the visit. On confirm it creates the claim in ${PMS} and submits it.`,
      },
      usuallyPays: 268,
      procedureGroup: 'cleaning and exam',
      procedures: [
        { id: 'p-1006b-1', code: 'D0120', description: 'Periodic oral evaluation', tooth: null, fee: 62 },
        { id: 'p-1006b-2', code: 'D0274', description: 'Bitewings, four films', tooth: null, fee: 82 },
        { id: 'p-1006b-3', code: 'D1110', description: 'Prophylaxis, adult', tooth: null, fee: 183 },
      ],
      changes: [],
      attachments: [],
      remarks: '',
      listLine: `No claim existed in ${PMS}`,
      history: createdHistory('Oct 7'),
    },

    // 4 — Jennifer Adams
    {
      id: 'CLM-1007',
      patient: 'Jennifer Adams',
      memberId: 'U77-310-492',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      serviceDate: '2026-10-07',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 7, 11:40 AM', reviewedAt: 'Oct 7, 11:52 AM' },
      usuallyPays: 1090,
      procedureGroup: 'crown',
      procedures: [
        { id: 'p-1007-1', code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#14', fee: 1295 },
        { id: 'p-1007-2', code: 'D2950', description: 'Core build-up, including any pins', tooth: '#14', fee: 285 },
      ],
      changes: [
        {
          id: 'ch-1007-1',
          title: 'Wrote a clinical narrative for the build-up',
          why: 'Cigna requires one when D2950 is billed with a crown.',
          applied: true,
          revert: {
            before: { narrativeText: '' },
            after: {
              narrativeText:
                'Tooth #14 has a large failing amalgam with recurrent decay on the mesial and occlusal surfaces. After removal, the remaining structure is insufficient to retain a crown without a core build-up. The periapical X-ray of Oct 7, 2026 shows the extent of the restoration and decay.',
            },
          },
        },
        {
          id: 'ch-1007-2',
          title: 'Attached the periapical X-ray from Oct 7',
          why: 'Cigna requires an X-ray for a crown. Found in the imaging taken that day.',
          applied: true,
          revert: {
            before: { removeAttachmentId: 'att-1007-xray' },
            after: {
              addAttachment: {
                id: 'att-1007-xray',
                name: 'Periapical X-ray',
                kind: 'xray',
                date: 'Oct 7, 2026',
                origin: 'added by ELVA',
              },
            },
          },
        },
      ],
      narrative: {
        code: 'D2950',
        state: 'elva',
        basis: 'the clinical note and X-ray of Oct 7',
        text:
          'Tooth #14 has a large failing amalgam with recurrent decay on the mesial and occlusal surfaces. After removal, the remaining structure is insufficient to retain a crown without a core build-up. The periapical X-ray of Oct 7, 2026 shows the extent of the restoration and decay.',
      },
      attachments: [
        { id: 'att-1007-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 7, 2026', origin: 'added by ELVA' },
      ],
      remarks: '',
      listLine: 'Narrative and X-ray added',
      learningOverride: {
        did: 'You added the fracture line from the clinical note to the narrative.',
        will: 'For Cigna crown claims, ELVA will now include fracture findings in the narrative.',
        scope: 'Cigna crown claims',
      },
      history: pmsHistory('Oct 7, 11:40 AM', 'Oct 7, 11:52 AM'),
    },

    // 5 — David Park
    {
      id: 'CLM-1009A',
      patient: 'David Park',
      memberId: 'U8810-7730',
      payer: { name: 'MetLife PDP', short: 'MetLife' },
      serviceDate: '2026-10-09',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 9, 2:05 PM', reviewedAt: 'Oct 9, 2:12 PM' },
      usuallyPays: 210,
      procedureGroup: 'extraction',
      procedures: [
        { id: 'p-1009a-1', code: 'D7140', description: 'Extraction, erupted tooth', tooth: '#16', fee: 240 },
      ],
      changes: [],
      checkedAgainst: {
        sentence:
          'Codes, tooth and the X-ray match MetLife’s rules and how MetLife paid your last 89 extractions.',
        label: '89 MetLife extractions',
        evidenceId: 'ev-metlife-7140',
      },
      attachments: [
        { id: 'att-1009a-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 9, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Checked against 89 MetLife claims',
      history: pmsHistory('Oct 9, 2:05 PM', 'Oct 9, 2:12 PM'),
    },

    // 6 — Tom Baker
    {
      id: 'CLM-1009B',
      patient: 'Tom Baker',
      memberId: 'W281-90-3318',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-10-09',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Oct 9, 4:40 PM', reviewedAt: 'Oct 9, 4:51 PM' },
      usuallyPays: 148,
      procedureGroup: 'filling',
      procedures: [
        { id: 'p-1009b-1', code: 'D2391', description: 'Composite, one surface, posterior', tooth: '#19', fee: 185 },
      ],
      changes: [],
      checkedAgainst: {
        sentence:
          'Code and tooth match Aetna’s rules and how Aetna paid your last 214 one-surface fillings.',
        label: '214 Aetna fillings',
        evidenceId: 'ev-aetna-2391',
      },
      attachments: [],
      remarks: '',
      listLine: 'Checked against 214 Aetna claims',
      history: pmsHistory('Oct 9, 4:40 PM', 'Oct 9, 4:51 PM'),
    },

    // 7 — Grace Kim
    {
      id: 'CLM-1010A',
      patient: 'Grace Kim',
      memberId: '6120-448-01',
      payer: { name: 'Guardian', short: 'Guardian' },
      serviceDate: '2026-10-10',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 10, 10:15 AM', reviewedAt: 'Oct 10, 10:24 AM' },
      usuallyPays: 190,
      procedureGroup: 'filling',
      procedures: [
        { id: 'p-1010a-1', code: 'D2393', description: 'Composite, three surfaces, posterior', tooth: '#30', fee: 264 },
      ],
      changes: [
        {
          id: 'ch-1010a-1',
          title: 'Sent the claim to Guardian instead of Cigna',
          why: `${PMS}’s draft was addressed to Cigna, which ended Sep 30. Guardian has been active since Oct 1.`,
          evidenceId: 'ev-kim-elig',
          evidenceLabel: 'Eligibility check, Oct 10',
          applied: true,
          revert: {
            before: { payer: { name: 'Cigna DPPO', short: 'Cigna' } },
            after: { payer: { name: 'Guardian', short: 'Guardian' } },
          },
          undoWarning: 'Cigna ended Sep 30 and will deny this claim.',
        },
      ],
      attachments: [],
      remarks: '',
      listLine: 'Changed the payer to Guardian',
      history: pmsHistory('Oct 10, 10:15 AM', 'Oct 10, 10:24 AM'),
    },

    // 8 — Emma Novak
    {
      id: 'CLM-1010B',
      patient: 'Emma Novak',
      memberId: '0917-62-8805',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      serviceDate: '2026-10-10',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 10, 1:30 PM', reviewedAt: 'Oct 10, 1:41 PM' },
      usuallyPays: 182,
      procedureGroup: 'exam',
      procedures: [
        { id: 'p-1010b-1', code: 'D0150', description: 'Comprehensive oral evaluation', tooth: null, fee: 89 },
        { id: 'p-1010b-2', code: 'D0210', description: 'X-rays, complete series', tooth: null, fee: 125 },
      ],
      changes: [],
      checkedAgainst: {
        sentence: 'Codes match Delta’s rules and how Delta paid your last 41 new-patient exams.',
        label: '41 Delta exams',
        evidenceId: 'ev-delta-0150',
      },
      attachments: [],
      remarks: '',
      listLine: 'Checked against 41 Delta claims',
      history: pmsHistory('Oct 10, 1:30 PM', 'Oct 10, 1:41 PM'),
    },

    // 9 — Omar Haddad
    {
      id: 'CLM-1012B',
      patient: 'Omar Haddad',
      memberId: 'U77-552-018',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      serviceDate: '2026-10-12',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Oct 12, 9:50 AM', reviewedAt: 'Oct 12, 10:02 AM' },
      usuallyPays: 410,
      procedureGroup: 'periodontal',
      procedures: [
        { id: 'p-1012b-1', code: 'D4341', description: 'Scaling and root planing, 4+ teeth per quadrant', tooth: 'UR', fee: 265 },
        { id: 'p-1012b-2', code: 'D4341', description: 'Scaling and root planing, 4+ teeth per quadrant', tooth: 'UL', fee: 265 },
      ],
      changes: [
        {
          id: 'ch-1012b-1',
          title: 'Attached the periodontal chart from Oct 12',
          why: 'Cigna requires pocket depths for scaling and root planing. Taken from the chart recorded that day.',
          applied: true,
          revert: {
            before: { removeAttachmentId: 'att-1012b-perio' },
            after: {
              addAttachment: {
                id: 'att-1012b-perio',
                name: 'Periodontal chart',
                kind: 'document',
                date: 'Oct 12, 2026',
                origin: 'added by ELVA',
              },
            },
          },
        },
      ],
      attachments: [
        { id: 'att-1012b-perio', name: 'Periodontal chart', kind: 'document', date: 'Oct 12, 2026', origin: 'added by ELVA' },
        { id: 'att-1012b-bw', name: 'Bitewings', kind: 'xray', date: 'Oct 12, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Perio chart attached',
      history: pmsHistory('Oct 12, 9:50 AM', 'Oct 12, 10:02 AM'),
    },

    // 10 — Luis Romero (ELVA created)
    {
      id: 'CLM-1013A',
      patient: 'Luis Romero',
      memberId: 'UC-3302-7714',
      payer: { name: 'United Concordia', short: 'United Concordia' },
      serviceDate: '2026-10-13',
      provider: 'Dr. Emily Carter',
      source: {
        kind: 'elva_created',
        reviewedAt: 'Oct 13',
        note:
          `The crown on #19 was seated on Oct 13 and the visit was checked out without a claim. ELVA built the claim from the visit. On confirm it creates the claim in ${PMS} and submits it.`,
      },
      usuallyPays: 880,
      procedureGroup: 'crown',
      procedures: [
        { id: 'p-1013a-1', code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#19', fee: 1295 },
      ],
      changes: [
        {
          id: 'ch-1013a-1',
          title: 'Attached the periapical X-ray from Oct 13',
          why: 'United Concordia requires an X-ray for a crown.',
          applied: true,
          revert: {
            before: { removeAttachmentId: 'att-1013a-xray' },
            after: {
              addAttachment: {
                id: 'att-1013a-xray',
                name: 'Periapical X-ray',
                kind: 'xray',
                date: 'Oct 13, 2026',
                origin: 'added by ELVA',
              },
            },
          },
        },
      ],
      attachments: [
        { id: 'att-1013a-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 13, 2026', origin: 'added by ELVA' },
      ],
      remarks: '',
      listLine: `No claim existed in ${PMS}`,
      history: createdHistory('Oct 13'),
    },

    // 11 — Daniel Reyes
    {
      id: 'CLM-1013B',
      patient: 'Daniel Reyes',
      memberId: 'W281-17-6604',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-10-13',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 13, 3:10 PM', reviewedAt: 'Oct 13, 3:22 PM' },
      usuallyPays: 905,
      procedureGroup: 'root canal',
      procedures: [
        { id: 'p-1013b-1', code: 'D3330', description: 'Root canal, molar', tooth: '#30', fee: 1240 },
      ],
      changes: [
        {
          id: 'ch-1013b-1',
          title: 'Attached the final X-ray from Oct 13',
          why: 'Aetna asks for a post-treatment X-ray for molar root canals. Found in the imaging taken that day.',
          applied: true,
          revert: {
            before: { removeAttachmentId: 'att-1013b-final' },
            after: {
              addAttachment: {
                id: 'att-1013b-final',
                name: 'Final periapical X-ray',
                kind: 'xray',
                date: 'Oct 13, 2026',
                origin: 'added by ELVA',
              },
            },
          },
        },
      ],
      attachments: [
        { id: 'att-1013b-final', name: 'Final periapical X-ray', kind: 'xray', date: 'Oct 13, 2026', origin: 'added by ELVA' },
        { id: 'att-1013b-preop', name: 'Pre-op periapical X-ray', kind: 'xray', date: 'Oct 13, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Final X-ray attached',
      history: pmsHistory('Oct 13, 3:10 PM', 'Oct 13, 3:22 PM'),
    },

    // 12 — Priya Shah
    {
      id: 'CLM-1013C',
      patient: 'Priya Shah',
      memberId: 'U8810-1290',
      payer: { name: 'MetLife PDP', short: 'MetLife' },
      serviceDate: '2026-10-13',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Oct 13, 11:05 AM', reviewedAt: 'Oct 13, 11:14 AM' },
      usuallyPays: 220,
      procedureGroup: 'cleaning and exam',
      procedures: [
        { id: 'p-1013c-1', code: 'D0120', description: 'Periodic oral evaluation', tooth: null, fee: 62 },
        { id: 'p-1013c-2', code: 'D1110', description: 'Prophylaxis, adult', tooth: null, fee: 183 },
      ],
      changes: [],
      checkedAgainst: {
        sentence: 'Codes match MetLife’s rules and how MetLife paid your last 312 cleanings and exams.',
        label: '312 MetLife cleanings',
        evidenceId: 'ev-metlife-1110',
      },
      attachments: [],
      remarks: '',
      listLine: 'Checked against 312 MetLife claims',
      history: pmsHistory('Oct 13, 11:05 AM', 'Oct 13, 11:14 AM'),
    },

    // 13 — Rafael Soto: Q13, may be a second visit or a duplicate; Cigna's filing limit is 6 days away
    // (D237). "It's a duplicate, don't bill" is a money action (D245): hidden for ?role=staff.
    {
      id: 'CLM-0726',
      patient: 'Rafael Soto',
      memberId: 'U77-310-4419',
      payer: { name: 'Cigna DPPO', short: 'Cigna' },
      serviceDate: '2026-07-26',
      provider: 'Dr. Olivia Brooks',
      source: {
        kind: 'elva_created',
        reviewedAt: 'Oct 16',
        note:
          `The filling on #19 was done on Jul 26 and the visit was checked out without a claim. ELVA built the claim from the visit. On confirm it creates the claim in ${PMS} and submits it.`,
      },
      usuallyPays: 148,
      procedureGroup: 'filling',
      procedures: [
        { id: 'p-0726-1', code: 'D2391', description: 'Composite, one surface, posterior', tooth: '#19', fee: 185 },
      ],
      decision: {
        title: 'This may be a second visit, or a duplicate',
        body:
          'Cigna already has a D2391 on #19 from Jul 24, billed under Dr. Emily Carter. This one is dated Jul 26, under Dr. Olivia Brooks. It may be a second visit, or the same filling entered twice.',
        options: [
          {
            id: 'opt-visit',
            label: 'It’s a separate visit, send',
            style: 'secondary',
            effect: 'none',
            resolvedText: 'it’s a separate visit, send it',
          },
          {
            id: 'opt-dup',
            label: 'It’s a duplicate, don’t bill',
            style: 'ghost',
            effect: 'dont_bill',
            resolvedText: 'it’s a duplicate, don’t bill',
          },
        ],
        evidence: [],
      },
      changes: [],
      timelyFiling: { limitIso: '2026-10-24', confirmed: true },
      attachments: [],
      remarks: '',
      listLine: 'May be a second visit, or a duplicate',
      history: createdHistory('Oct 16'),
    },

    // 14 — Hector Villanueva: Aetna's filing limit is 10 days away; ELVA just sends (D237)
    {
      id: 'CLM-0730',
      patient: 'Hector Villanueva',
      memberId: 'W281-52-1904',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-07-30',
      provider: 'Dr. Emily Carter',
      source: {
        kind: 'elva_created',
        reviewedAt: 'Oct 16',
        note:
          `The exam and cleaning on Jul 30 were never billed. ELVA found the visit and built the claim. On confirm it creates the claim in ${PMS} and submits it.`,
      },
      usuallyPays: 205,
      procedureGroup: 'cleaning and exam',
      procedures: [
        { id: 'p-0730-1', code: 'D0120', description: 'Periodic oral evaluation', tooth: null, fee: 62 },
        { id: 'p-0730-2', code: 'D1110', description: 'Prophylaxis, adult', tooth: null, fee: 183 },
      ],
      changes: [],
      checkedAgainst: {
        sentence: 'Codes match Aetna’s rules and how Aetna paid your last 214 cleanings and exams.',
        label: '214 Aetna claims',
        evidenceId: 'ev-ready-filler',
      },
      timelyFiling: { limitIso: '2026-10-28', confirmed: true },
      attachments: [],
      remarks: '',
      listLine: `No claim existed in ${PMS}`,
      history: createdHistory('Oct 16'),
    },

    // 15 — Grace Okonkwo: United Concordia's filing limit isn't researched, so the date is ELVA's
    // default and the row says "not confirmed" (D237).
    {
      id: 'CLM-0802',
      patient: 'Grace Okonkwo',
      memberId: 'UC-4418-9026',
      payer: { name: 'United Concordia', short: 'United Concordia' },
      serviceDate: '2026-08-02',
      provider: 'Dr. Olivia Brooks',
      source: {
        kind: 'elva_created',
        reviewedAt: 'Oct 16',
        note:
          `The filling on #14 was done on Aug 2 and never billed. ELVA found the visit and built the claim. On confirm it creates the claim in ${PMS} and submits it.`,
      },
      usuallyPays: 190,
      procedureGroup: 'filling',
      procedures: [
        { id: 'p-0802-1', code: 'D2392', description: 'Composite, two surfaces, posterior', tooth: '#14', fee: 232 },
      ],
      changes: [],
      checkedAgainst: {
        sentence: 'Code and tooth match United Concordia’s rules and how United Concordia paid your last 58 fillings.',
        label: '58 United Concordia fillings',
        evidenceId: 'ev-ready-filler',
      },
      timelyFiling: { limitIso: '2026-10-31', confirmed: false },
      attachments: [],
      remarks: '',
      listLine: `No claim existed in ${PMS}`,
      history: createdHistory('Oct 16'),
    },

    // 16 — Carmen Alvarez: ELVA added a finding the chart doesn't have (read from the X-ray), so the
    // claim is held for Dr. Lee's approval (D176). Held: not in the waiting count or the headline.
    {
      id: 'CLM-1015',
      patient: 'Carmen Alvarez',
      memberId: 'W281-61-5530',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-10-15',
      provider: 'Dr. Marcus Lee',
      source: { kind: 'pms_draft', createdAt: 'Oct 15, 1:05 PM', reviewedAt: 'Oct 15, 1:16 PM' },
      usuallyPays: 905,
      procedureGroup: 'root canal',
      procedures: [
        { id: 'p-1015-1', code: 'D3330', description: 'Root canal, molar', tooth: '#19', fee: 1240 },
      ],
      changes: [
        {
          id: 'ch-1015-1',
          title: 'Wrote a clinical narrative for the root canal',
          why: 'Aetna requires one for a molar root canal. ELVA added the finding it read from the X-ray.',
          applied: true,
          revert: {
            before: { narrativeText: '' },
            after: {
              narrativeText:
                'Tooth #19 has a deep distal restoration with recurrent decay reaching the pulp. The periapical X-ray of Oct 15, 2026 shows a periapical radiolucency at the apex of the mesial root. Root canal treatment is needed to retain the tooth.',
            },
          },
          undoWarning: 'Aetna requires a narrative for a molar root canal. Write one below or the claim may be denied.',
        },
      ],
      narrative: {
        code: 'D3330',
        state: 'elva',
        basis: 'the clinical note and X-ray of Oct 15',
        text:
          'Tooth #19 has a deep distal restoration with recurrent decay reaching the pulp. The periapical X-ray of Oct 15, 2026 shows a periapical radiolucency at the apex of the mesial root. Root canal treatment is needed to retain the tooth.',
      },
      attachments: [
        { id: 'att-1015-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Oct 15, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      held: { reason: DENTIST_APPROVAL_HOLD },
      findingApproval: {
        dentist: 'Dr. Marcus Lee',
        finding: 'Periapical radiolucency at the apex of the mesial root of #19',
        source: 'the periapical X-ray of Oct 15',
        status: 'needed',
      },
      listLine: 'ELVA added a finding the chart doesn’t have',
      history: pmsHistory('Oct 15, 1:05 PM', 'Oct 15, 1:16 PM'),
    },

    // Ready-to-confirm tail — generated so the queue's load-more is demoable. 25 plus the four
    // claims above keep the queue at 40 waiting (the fourth is held, so it is not counted).
    ...buildReadyFiller(25),
  ];
}

export function buildAutopilotClaims(): Claim[] {
  return [
    // Nadia Ferreira
    {
      id: 'CLM-1116A',
      patient: 'Nadia Ferreira',
      memberId: '0917-71-5520',
      payer: { name: 'Delta Dental PPO', short: 'Delta' },
      serviceDate: '2026-11-16',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Nov 16, 2:40 PM', reviewedAt: 'Nov 16, 2:51 PM' },
      usuallyPays: 1610,
      procedureGroup: 'implant',
      procedures: [
        { id: 'p-1116a-1', code: 'D6010', description: 'Implant body, endosteal', tooth: '#19', fee: 2140 },
      ],
      changes: [],
      attachments: [
        { id: 'att-1116a-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Nov 16, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Not enough Delta implant claims yet',
      heldShort: 'Not enough Delta implant claims yet',
      heldReason:
        'ELVA has seen 2 implant claims for Delta Dental. It needs 10 before it submits them on its own.',
      history: pmsHistory('Nov 16, 2:40 PM', 'Nov 16, 2:51 PM'),
    },
    // Rosa Delgado
    {
      id: 'CLM-1116B',
      patient: 'Rosa Delgado',
      memberId: 'TXM-5510-224',
      payer: { name: 'Texas Medicaid', short: 'Texas Medicaid' },
      serviceDate: '2026-11-16',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Nov 16, 10:05 AM', reviewedAt: 'Nov 16, 10:14 AM' },
      usuallyPays: null,
      procedureGroup: 'cleaning and exam',
      procedures: [
        { id: 'p-1116b-1', code: 'D0120', description: 'Periodic oral evaluation', tooth: null, fee: 62 },
        { id: 'p-1116b-2', code: 'D1206', description: 'Fluoride varnish', tooth: null, fee: 56 },
      ],
      changes: [],
      attachments: [],
      remarks: '',
      listLine: 'New payer for this practice',
      heldShort: 'New payer for this practice',
      heldReason: 'New payer for this practice. No Medicaid claims confirmed yet.',
      history: pmsHistory('Nov 16, 10:05 AM', 'Nov 16, 10:14 AM'),
    },
    // Kevin Ng
    {
      id: 'CLM-1117A',
      patient: 'Kevin Ng',
      memberId: 'W281-33-7781',
      payer: { name: 'Aetna PPO', short: 'Aetna' },
      serviceDate: '2026-11-17',
      provider: 'Dr. Emily Carter',
      source: { kind: 'pms_draft', createdAt: 'Nov 17, 3:30 PM', reviewedAt: 'Nov 17, 3:42 PM' },
      usuallyPays: 820,
      procedureGroup: 'crown',
      procedures: [
        { id: 'p-1117a-1', code: 'D2750', description: 'Crown, porcelain fused to high noble metal', tooth: '#3', fee: 1150 },
      ],
      decision: {
        title: 'The narrative mentions a crack that isn’t in the notes',
        body:
          'ELVA’s narrative says tooth #3 has a cracked cusp, which Aetna needs to see to pay for a crown. Dr. Carter’s note for Nov 17 doesn’t mention a crack. A finding has to come from the dentist’s note: remove it, or hold until Dr. Carter adds it.',
        options: [
          {
            id: 'opt-remove',
            label: 'Remove it from the narrative',
            style: 'secondary',
            effect: 'edit_narrative',
            narrative:
              'Tooth #3 has a large existing amalgam with recurrent decay. A full-coverage crown is needed to restore function.',
            resolvedText: 'remove the crack from the narrative',
          },
          {
            id: 'opt-hold-note',
            label: 'Hold until the dentist adds it to the note',
            style: 'ghost',
            effect: 'hold',
            holdReason: 'Waiting for the dentist',
            holdNote: 'Dr. Carter adds the crack to her note for tooth #3',
            resolvedText: 'hold until the dentist adds it to the note',
          },
        ],
        evidence: [],
      },
      changes: [],
      narrative: {
        code: 'D2750',
        state: 'elva',
        basis: 'the clinical note of Nov 17',
        text:
          'Tooth #3 has a cracked mesiolingual cusp and a large existing amalgam with recurrent decay. A full-coverage crown is needed to prevent the crack from propagating and to restore function.',
      },
      attachments: [
        { id: 'att-1117a-xray', name: 'Periapical X-ray', kind: 'xray', date: 'Nov 17, 2026', origin: `from ${PMS}` },
      ],
      remarks: '',
      listLine: 'Narrative needs a finding ELVA can’t find',
      heldShort: 'Narrative needs a finding ELVA can’t find',
      heldReason: 'The narrative depends on a finding ELVA could not find in the clinical note.',
      history: pmsHistory('Nov 17, 3:30 PM', 'Nov 17, 3:42 PM'),
    },
    // Hannah Price
    {
      id: 'CLM-1117B',
      patient: 'Hannah Price',
      memberId: '6120-559-02',
      payer: { name: 'Guardian', short: 'Guardian' },
      serviceDate: '2026-11-17',
      provider: 'Dr. Olivia Brooks',
      source: { kind: 'pms_draft', createdAt: 'Nov 17, 9:20 AM', reviewedAt: 'Nov 17, 9:31 AM' },
      usuallyPays: 182,
      procedureGroup: 'exam',
      procedures: [
        { id: 'p-1117b-1', code: 'D0150', description: 'Comprehensive oral evaluation', tooth: null, fee: 89 },
        { id: 'p-1117b-2', code: 'D0210', description: 'X-rays, complete series', tooth: null, fee: 125 },
      ],
      changes: [
        {
          id: 'ch-1117b-1',
          title: 'Sent the claim to Guardian instead of Cigna',
          why: `${PMS}’s draft was addressed to Cigna, which ended Oct 31. Guardian has been active since Nov 1.`,
          evidenceId: 'ev-price-elig',
          evidenceLabel: 'Eligibility check, Nov 17',
          applied: true,
          revert: {
            before: { payer: { name: 'Cigna DPPO', short: 'Cigna' } },
            after: { payer: { name: 'Guardian', short: 'Guardian' } },
          },
          undoWarning: 'Cigna ended Oct 31 and will deny this claim.',
        },
      ],
      attachments: [],
      remarks: '',
      listLine: 'Payer changed to Guardian',
      heldShort: 'Payer changed to Guardian',
      heldReason:
        'Insurance changed: Cigna ended Oct 31, so ELVA moved the claim to Guardian. A change of payer always comes to you.',
      history: pmsHistory('Nov 17, 9:20 AM', 'Nov 17, 9:31 AM'),
    },
  ];
}

/** Static figures for the autopilot Today card (§14.4). */
export const AUTOPILOT_TODAY = { claims: 23, sum: 6410 };
